import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'bp_token';
const USER_CACHE_KEY = 'bp_user_cache';

axios.defaults.withCredentials = true;

// Global axios: sends Bearer token when available (mobile PWA cookie fallback)
axios.interceptors.request.use((cfg) => {
  try {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t && cfg.url && cfg.url.startsWith(API) && !cfg.headers.Authorization) {
      cfg.headers.Authorization = `Bearer ${t}`;
    }
  } catch {}
  return cfg;
});

const saveToken = (t) => { try { if (t) localStorage.setItem(TOKEN_KEY, t); } catch {} };
const clearToken = () => { try { localStorage.removeItem(TOKEN_KEY); } catch {} };

// Cache user payload so we can render instantly on next boot while /auth/me revalidates.
const APP_SESSION_KEY = 'bp_app_session';
// App instalado (PWA): cada nova abertura começa na tela de login, mesmo com sessão salva.
const isStandalone = () => { try { return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true; } catch { return false; } };
const pwaRequiresLogin = () => { try { return (JSON.parse(localStorage.getItem('bp_app_settings')) || {}).security?.pwa_require_login !== false; } catch { return true; } };
const FRESH_LAUNCH = isStandalone() && pwaRequiresLogin() && !sessionStorage.getItem(APP_SESSION_KEY);
const saveUserCache = (u) => { try { if (u) { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u)); sessionStorage.setItem(APP_SESSION_KEY, '1'); } } catch {} };
const clearUserCache = () => { try { localStorage.removeItem(USER_CACHE_KEY); } catch {} };
const loadUserCache = () => {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw);
    // Only trust cached user if we also have a token — otherwise the session is stale/logged-out.
    if (!localStorage.getItem(TOKEN_KEY)) return null;
    return u;
  } catch { return null; }
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Hydrate immediately from localStorage cache → UI renders on first paint
  const [user, setUser] = useState(() => (FRESH_LAUNCH ? null : loadUserCache()));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null); // { role, allow_all, module_permissions }

  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me/permissions`, { withCredentials: true });
      setPermissions(data);
    } catch { setPermissions(null); }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      saveUserCache(data);
      await loadPermissions();
    } catch {
      setUser(null);
      clearToken();
      clearUserCache();
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [loadPermissions]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (FRESH_LAUNCH && !sessionStorage.getItem(APP_SESSION_KEY)) {
      // Só exige login no app; não encerra sessões do navegador no mesmo aparelho.
      setUser(null); setPermissions(null); setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithPassword = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    saveToken(data.token);
    setUser(data.user);
    saveUserCache(data.user);
    await loadPermissions();
    return data.user;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    clearToken();
    clearUserCache();
    setUser(null);
    setPermissions(null);
  };

  const logoutAll = async () => {
    const { data } = await axios.post(`${API}/auth/logout-all`, {}, { withCredentials: true });
    clearToken();
    clearUserCache();
    setUser(null);
    setPermissions(null);
    return data;
  };

  const setUserAndCache = (u) => { setUser(u); if (u) saveUserCache(u); else clearUserCache(); };

  // Helper centralizado para checar permissão de acesso ao módulo (path)
  const canAccess = useCallback((path) => {
    if (!permissions) return true; // ainda carregando — não bloqueia
    if (permissions.allow_all) return true;
    const allowed = permissions.module_permissions || [];
    return allowed.includes(path);
  }, [permissions]);

  return (
    <AuthContext.Provider value={{ user, loading, permissions, loginWithPassword, logout, logoutAll, setUser: setUserAndCache, checkAuth, loadPermissions, canAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { API };
